import { useClickListener } from 'lib/events-framework/useClickListener'
import { getValueForBreakpoint } from 'lib/get-value-for-breakpoint'
import { useResizeObserver } from 'lib/use-resize-observer'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'

export type Props = {
  onClickOutside: () => void
  onSetWidth?: (value: number) => void
  naturalHeight?: null | number
  naturalWidth?: null | number
  ratio?: { x: number; y: number }
}

export const PreviewSlide: FC<Props> = ({
  children,
  onClickOutside,
  onSetWidth = () => {},
  naturalHeight = null,
  naturalWidth = null,
  ratio = { x: 16, y: 9 }
}) => {
  const wrapperRef = useRef(null)
  const [width, setWidth] = useState(0)
  const activeBreakpoint = useTheme().activeBreakpoint

  const clickHandler = useCallback(
    (e) => {
      if (e.target === wrapperRef.current) onClickOutside()
    },
    [onClickOutside]
  )

  useClickListener(wrapperRef, clickHandler)

  useResizeObserver(wrapperRef, () => {
    // forcedVerticalEmptySpace - это минимальная сумма нижнего и верхнего расстояния от края экрана до картинки.
    const forcedVerticalEmptySpace = 120
    const documentHeight = document.documentElement.clientHeight
    const imageHeight =
      // getValueForBreakpoint({ phone: 5, tablet: 100 }, activeBreakpoint) позволяет нам задать горизонтальные отспуты
      (document.documentElement.clientWidth - getValueForBreakpoint({ phone: 5, tablet: 100 }, activeBreakpoint)) *
      (ratio.y / ratio.x)
    const verticalEmptySpace = documentHeight - imageHeight
    let additionalPixels = 0
    if (verticalEmptySpace < forcedVerticalEmptySpace) {
      additionalPixels = forcedVerticalEmptySpace - verticalEmptySpace
    }

    setWidth(
      Math.min(
        (document.documentElement.clientHeight - forcedVerticalEmptySpace) / (ratio.y / ratio.x),
        (imageHeight + additionalPixels) / (ratio.y / ratio.x),
        naturalWidth !== null ? naturalWidth : +Infinity
      )
    )
  })

  useEffect(() => onSetWidth(width), [width, onSetWidth])

  return (
    <Wrapper ref={wrapperRef} className="js-wrapper">
      <Content width={width}>{children}</Content>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`

const Content = styled.div<{ width: number }>`
  /* Нужно задавать max-width, т.к. картинка имеет padding-bottom: 56.25%;, то есть ее высота зависит от ее ширины.
     Если ширина будет слишком большая, то картинка может вылезти за край экрана по высоте.
     Чтобы этого избежать, мы задаем ее враперу max-width: 150vh, из-за максимальная высота картинки будет
     84.375vh;
     При этом мы не можем просто задать padding, высота картинки не будет им ограничиваться */
  width: ${({ width }) => `${width}px`};
  margin: auto;
`
